import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/**
 * FRONT-3223 (eddy, 2026-10-28): while a chat is empty its reply box sits in
 * the middle of the window and slides down when the first message goes. The
 * box stays at the bottom in the layout; `lift` is how far to raise it — half
 * the conversation area's height (less any content the box must stay below),
 * which puts it mid-window. Re-measured whenever that area resizes: a window
 * measures 0 the moment it opens (its slot is laid out afterwards), and a
 * resize must keep the box centred.
 *
 * `animate` is off until the box has been placed once: a new chat must open
 * with the box already in the middle, never fly up into it (eddy, 2026-10-28)
 * — only the slide down after the first message animates.
 */
export function useCenterLift(
  area: RefObject<HTMLElement | null>,
  active: boolean,
  /** Content at the top of `area` the box must stay below (the new Build's name and intro): it is centred in what is left. */
  occupied?: RefObject<HTMLElement | null>,
): { lift: number; animate: boolean; placing: boolean } {
  const [lift, setLift] = useState(0);
  const [animate, setAnimate] = useState(false);
  const [everPlaced, setEverPlaced] = useState(false);
  const placed = useRef(false);
  useLayoutEffect(() => {
    const el = area.current;
    if (!active || !el) {
      setLift(0);
      return;
    }
    const measure = () => {
      const next = Math.max(0, (el.clientHeight - (occupied?.current?.offsetHeight ?? 0)) / 2);
      setLift(next);
      // Turn the slide on only after the first real placement has painted.
      if (next > 0 && !placed.current) {
        placed.current = true;
        setEverPlaced(true);
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (occupied?.current) ro.observe(occupied.current);
    return () => ro.disconnect();
  }, [area, active, occupied]);
  // Until the first measure lands, the box is not where it belongs: keep it
  // invisible for those few frames rather than flash it at the bottom. Only
  // then: once placed, a lift of 0 is real (the box was expanded and the area
  // above it shrank to nothing) and the box must stay visible (eddy,
  // 2026-10-28: expanding a centred box made it disappear).
  return { lift, animate, placing: active && !everPlaced };
}

/**
 * The classes for the lifted element: hidden while placing, the slide only
 * once placed. The raise itself is a class reading `--center-lift`
 * (`liftStyle`), so an expanded reply box (`data-expanded`, the box's own
 * "make large") drops it: the box comes down and grows in one move instead of
 * growing upwards off the window (eddy, 2026-10-28).
 */
export function liftTransition(animate: boolean, placing = false): string {
  const raise = 'translate-y-(--center-lift) has-[[data-expanded=true]]:translate-y-0';
  if (placing) return `invisible ${raise}`;
  return animate ? `${raise} transition-[translate] duration-500 ease-out motion-reduce:transition-none` : raise;
}

/** The raise for `liftTransition`'s class, as a CSS variable (none when not raised). */
export function liftStyle(lift: number): CSSProperties | undefined {
  return lift ? ({ '--center-lift': `-${lift}px` } as CSSProperties) : undefined;
}

/** How long the slide down takes (matches `duration-500` above). */
export const LIFT_SLIDE_MS = 500;

/**
 * FRONT-3223 (eddy, 2026-10-28: "it should move down, then resize"): a
 * centred box asked to expand first drops to the bottom, and only then grows.
 * Returns the `onExpandChange` for `ChatInput` and whether the box is held
 * down; while held, the caller passes a lift of 0.
 */
export function useExpandDrop(lift: number): { held: boolean; onExpandChange: (next: boolean) => number } {
  const [held, setHeld] = useState(false);
  return {
    held,
    onExpandChange: (next) => {
      setHeld(next);
      return next && lift > 0 ? LIFT_SLIDE_MS : 0;
    },
  };
}
